import { Switch } from '@hive/component';
import { AuthService, SubscriptionService } from '@hive/core/modules/cloud';
import { SubscriptionPlan, SubscriptionRecurring } from '@hive/graphql';
import { Trans, useI18n } from '@hive/i18n';
import { useLiveData, useServices } from '@toeverything/infra';
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CloudPlanLayout } from './layout';
import { PlanCard } from './plan-card';
import { planTitleTitleCaption } from './style.css';
import * as styles from './style.css';

type T = ReturnType<typeof useI18n>;

export type Benefits = Record<
  string,
  Array<{
    icon?: ReactNode;
    title: ReactNode;
  }>
>;
type BenefitsGetter = (t: T) => Benefits;
interface BasePrice {
  plan: SubscriptionPlan;
  name: string;
  description: string;
  benefits: Benefits;
}
export interface FixedPrice extends BasePrice {
  type: 'fixed';
  price: string;
  yearlyPrice: string;
  discount?: string;
  titleRenderer: (
    recurring: SubscriptionRecurring,
    detail: FixedPrice
  ) => ReactNode;
}

export interface DynamicPrice extends BasePrice {
  type: 'dynamic';
  contact: boolean;
  titleRenderer: (
    recurring: SubscriptionRecurring,
    detail: DynamicPrice
  ) => ReactNode;
}

const teamBenefits: BenefitsGetter = t => ({
  [t['com.hive.payment.cloud.team-workspace.benefit.g1']()]: [
    {
      title: t['com.hive.payment.cloud.team-workspace.benefit.g1-1'](),
    },
    ...([2, 3, 4, 5, 6] as const).map(i => ({
      title: t[`com.hive.payment.cloud.team-workspace.benefit.g1-${i}`](),
    })),
  ],
});

export function getPlanDetail(t: T) {
  return new Map<SubscriptionPlan, FixedPrice | DynamicPrice>([
    [
      SubscriptionPlan.Team,
      {
        type: 'fixed',
        plan: SubscriptionPlan.Team,
        price: '18',
        yearlyPrice: '15',
        name: t['com.hive.payment.cloud.team-workspace.name'](),
        description: t['com.hive.payment.cloud.team-workspace.description'](),
        titleRenderer: (recurring, detail) => {
          const price =
            recurring === SubscriptionRecurring.Yearly
              ? detail.yearlyPrice
              : detail.price;
          return (
            <>
              {t['com.hive.payment.cloud.team-workspace.title.price-monthly']({
                price: '$' + price,
              })}
              {recurring === SubscriptionRecurring.Yearly ? (
                <span className={planTitleTitleCaption}>
                  {t[
                    'com.hive.payment.cloud.team-workspace.title.billed-yearly'
                  ]()}
                </span>
              ) : null}
            </>
          );
        },
        benefits: teamBenefits(t),
      },
    ],
  ]);
}

const getRecurringLabel = ({
  recurring,
  t,
}: {
  recurring: SubscriptionRecurring;
  t: ReturnType<typeof useI18n>;
}) => {
  return recurring === SubscriptionRecurring.Monthly
    ? t['com.hive.payment.recurring-monthly']()
    : t['com.hive.payment.recurring-yearly']();
};

export const CloudPlans = () => {
  const t = useI18n();
  const scrollWrapper = useRef<HTMLDivElement>(null);

  const { authService, subscriptionService } = useServices({
    AuthService,
    SubscriptionService,
  });

  const prices = useLiveData(subscriptionService.prices.prices$);
  const loggedIn = useLiveData(authService.session.status$) === 'authenticated';
  // Use team subscription as the main subscription
  const teamSubscription = useLiveData(subscriptionService.subscription.pro$);

  const [recurring, setRecurring] = useState<SubscriptionRecurring>(
    teamSubscription?.recurring ?? SubscriptionRecurring.Yearly
  );

  const planDetail = useMemo(() => {
    const rawMap = getPlanDetail(t);
    const clonedMap = new Map<SubscriptionPlan, FixedPrice | DynamicPrice>();

    rawMap.forEach((detail, plan) => {
      clonedMap.set(plan, { ...detail });
    });

    prices?.forEach(price => {
      const detail = clonedMap.get(price.plan);

      if (detail?.type === 'fixed') {
        detail.price = ((price.amount ?? 0) / 100).toFixed(2);
        detail.yearlyPrice = ((price.yearlyAmount ?? 0) / 100 / 12).toFixed(2);
        detail.discount =
          price.yearlyAmount && price.amount
            ? Math.floor(
                (1 - price.yearlyAmount / 12 / price.amount) * 100
              ).toString()
            : undefined;
      }
    });

    return clonedMap;
  }, [prices, t]);

  const currentPlan = teamSubscription?.plan ?? null;
  const isCanceled = !!teamSubscription?.canceledAt;
  const currentRecurring =
    teamSubscription?.recurring ?? SubscriptionRecurring.Monthly;
  const yearlyDiscount = (
    planDetail.get(SubscriptionPlan.Team) as FixedPrice | undefined
  )?.discount;

  // auto scroll to current plan card
  useEffect(() => {
    if (!scrollWrapper.current) return;
    const currentPlanCard = scrollWrapper.current.querySelector(
      '[data-current="true"]'
    );
    const wrapperComputedStyle = getComputedStyle(scrollWrapper.current);
    const left = currentPlanCard
      ? currentPlanCard.getBoundingClientRect().left -
        scrollWrapper.current.getBoundingClientRect().left -
        parseInt(wrapperComputedStyle.paddingLeft)
      : 0;
    const appeared = scrollWrapper.current.dataset.appeared === 'true';
    const animationFrameId = requestAnimationFrame(() => {
      if (!scrollWrapper.current) return;
      scrollWrapper.current.scrollTo({
        behavior: appeared ? 'smooth' : 'instant',
        left,
      });
      scrollWrapper.current.dataset.appeared = 'true';
    });
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [recurring]);

  // caption - show trial info for non-subscribers
  const cloudCaption = loggedIn ? (
    currentPlan ? (
      isCanceled ? (
        <p>
          {t['com.hive.payment.subtitle-canceled']({
            plan: `${getRecurringLabel({
              recurring: currentRecurring,
              t,
            })} ${currentPlan}`,
          })}
        </p>
      ) : (
        <p>
          <Trans
            plan={currentPlan}
            i18nKey="com.hive.payment.subtitle-active"
            values={{ currentPlan }}
          >
            You are currently on the {{ currentPlan }} plan. If you have any
            questions, please contact our&nbsp;
            <a
              href="mailto:support@toeverything.info"
              style={{ color: 'var(--hive-link-color)' }}
            >
              customer support
            </a>
            .
          </Trans>
        </p>
      )
    ) : (
      <p>Start your 7-day free trial today. Credit card required.</p>
    )
  ) : (
    <p>{t['com.hive.payment.subtitle-not-signed-in']()}</p>
  );

  // toggle
  const cloudToggle = (
    <div className={styles.recurringToggleWrapper}>
      <div>
        <div className={styles.recurringToggleRecurring}>
          <span>
            {t['com.hive.payment.cloud.pricing-plan.toggle-billed-yearly']()}
          </span>
        </div>
        {yearlyDiscount ? (
          <div className={styles.recurringToggleDiscount}>
            {t['com.hive.payment.cloud.pricing-plan.toggle-discount']({
              discount: yearlyDiscount,
            })}
          </div>
        ) : null}
      </div>
      <Switch
        checked={recurring === SubscriptionRecurring.Yearly}
        onChange={checked =>
          setRecurring(
            checked
              ? SubscriptionRecurring.Yearly
              : SubscriptionRecurring.Monthly
          )
        }
      />
    </div>
  );

  const cloudScroll = (
    <div className={styles.planCardsWrapper} ref={scrollWrapper}>
      {Array.from(planDetail.values()).map(detail => {
        return <PlanCard key={detail.plan} {...{ detail, recurring }} />;
      })}
    </div>
  );

  const cloudSelect = (
    <div className={styles.cloudSelect}>
      <b>{t['com.hive.payment.cloud.pricing-plan.select.title']()}</b>
      <span>{t['com.hive.payment.cloud.pricing-plan.select.caption']()}</span>
    </div>
  );

  return (
    <CloudPlanLayout
      caption={cloudCaption}
      select={cloudSelect}
      toggle={cloudToggle}
      scroll={cloudScroll}
      scrollRef={scrollWrapper as RefObject<HTMLDivElement>}
      lifetime={null}
    />
  );
};
